import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, ChevronRight, X, Sparkles, Gift } from "lucide-react";

/**
 * FloatingPoints — always-on champagne puck bottom-right of every page.
 *
 * Two states:
 *  1. Signed in  (localStorage.client_token present)
 *      → fetches /api/client/points-summary, shows live balance + "$X credit"
 *      → click expands to a card with a big REDEEM button (deep-links to
 *        /my-appointments?focus=redeem)
 *  2. Signed out
 *      → small "See My Points" hint, click routes to /my-appointments
 *
 * Auto-pulses softly every ~75s to draw the eye without being annoying. Honors
 * a per-session "dismissed" flag (sessionStorage) so power users can hide it.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_DISMISSED = "kl_floating_points_dismissed_v1";
const PULSE_EVERY_MS = 75_000;
const PULSE_FOR_MS = 2_400;

export default function FloatingPoints({ language = "en" }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isFr = language === "fr";
  const t = useMemo(
    () => ({
      mini: isFr ? "Mes Points" : "My Points",
      signedOutTitle: isFr ? "Voir mes points Couronne" : "See My Crown Points",
      signedOutBody: isFr
        ? "Connectez-vous pour voir votre solde et échanger."
        : "Sign in to view your balance and redeem.",
      signedOutCta: isFr ? "Se connecter" : "Sign In",
      youHave: isFr ? "Vous avez" : "You have",
      pointsAvailable: isFr ? "points disponibles" : "points available",
      currentCredit: isFr ? "Crédit disponible" : "Credit available",
      readyToRedeem: isFr ? "PRÊT À ÉCHANGER" : "READY TO REDEEM",
      redeemNow: isFr ? "ÉCHANGER MAINTENANT" : "REDEEM NOW",
      keepGoing: isFr ? "Continuez" : "Keep going",
      toNextTier: isFr ? "points pour le prochain palier" : "points to next tier",
      viewDashboard: isFr ? "Voir le tableau de bord" : "View dashboard",
      dismiss: isFr ? "Fermer" : "Dismiss",
    }),
    [isFr]
  );

  // Pick up dismissed state + token once on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_DISMISSED) === "1") {
      setDismissed(true);
    }
    setToken(localStorage.getItem("client_token") || null);

    // React to login/logout in other tabs or the same tab.
    const sync = () => setToken(localStorage.getItem("client_token") || null);
    window.addEventListener("storage", sync);
    window.addEventListener("kl-auth-change", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("kl-auth-change", sync);
    };
  }, []);

  // Fetch the lightweight points summary when token is present.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setData(null);
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(`${API}/client/points-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        // Token might be expired — silently degrade to logged-out state.
        if (e?.response?.status === 401) {
          localStorage.removeItem("client_token");
          if (!cancelled) setToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Soft pulse cadence — only when collapsed and not dismissed.
  useEffect(() => {
    if (dismissed || expanded) return;
    const tick = () => {
      setPulse(true);
      setTimeout(() => setPulse(false), PULSE_FOR_MS);
    };
    // First pulse 12s after mount so it doesn't fight with page entrance.
    const first = setTimeout(tick, 12_000);
    const id = setInterval(tick, PULSE_EVERY_MS);
    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, [dismissed, expanded]);

  if (dismissed) return null;

  const isLoggedIn = !!token;
  const points = data?.points_available ?? 0;
  const creditValue = data?.current_credit_value ?? 0;
  const canRedeem = !!data?.can_redeem;
  const nextTier = data?.next_tier;
  const pointsToNext = data?.points_to_next_tier ?? 0;

  const handlePuckClick = () => {
    if (!isLoggedIn) {
      // Skip the expansion dance — just route them to sign-in.
      navigate("/my-appointments");
      return;
    }
    setExpanded((v) => !v);
  };

  const handleRedeem = () => {
    setExpanded(false);
    navigate("/my-appointments?focus=redeem");
  };

  const handleDashboard = () => {
    setExpanded(false);
    navigate("/my-appointments?focus=points");
  };

  const dismiss = (e) => {
    e?.stopPropagation();
    sessionStorage.setItem(STORAGE_DISMISSED, "1");
    setDismissed(true);
  };

  // Floating puck is fully retired — replaced by the Navbar's header pill
  // (desktop) and bottom-nav Crown badge (mobile). Render nothing.
  return null;
  /* eslint-disable no-unreachable */
  return (
    <div
      className="fixed z-[90] right-4 sm:right-6 hidden"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)" }}
      data-testid="floating-points-root"
    >
      {/* Expanded card */}
      {expanded && isLoggedIn && (
        <div
          className="mb-3 w-[300px] max-w-[calc(100vw-2rem)] rounded-2xl border border-amber-300/60 bg-gradient-to-br from-stone-900 via-zinc-900 to-stone-900 shadow-2xl shadow-amber-900/40 overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200"
          data-testid="floating-points-card"
        >
          {/* Header */}
          <div className="relative px-5 pt-5 pb-4 bg-gradient-to-br from-amber-500/15 via-amber-400/5 to-transparent border-b border-amber-300/20">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="absolute top-2 right-2 w-7 h-7 rounded-full text-amber-200/70 hover:text-amber-100 hover:bg-amber-500/15 flex items-center justify-center transition-colors"
              data-testid="floating-points-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-1.5">
              <Crown className="w-4 h-4 text-amber-300" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300/90">
                Crown Reserve
              </span>
            </div>
            {loading && !data ? (
              <div className="h-12 w-32 rounded bg-stone-800/60 animate-pulse" />
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-4xl font-light text-white tabular-nums tracking-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    data-testid="floating-points-balance"
                  >
                    {points.toLocaleString()}
                  </span>
                  <span className="text-xs text-amber-200/70 font-medium uppercase tracking-wider">
                    {isFr ? "pts" : "pts"}
                  </span>
                </div>
                <p className="text-xs text-stone-300 mt-1">
                  {t.youHave}{" "}
                  <span className="text-amber-200 font-semibold">
                    ${creditValue}
                  </span>{" "}
                  {isFr ? "en crédit" : "in credit"}
                </p>
              </>
            )}
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            {canRedeem ? (
              <>
                <div className="flex items-center gap-1.5 mb-3">
                  <Sparkles className="w-3 h-3 text-amber-300" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-300/90">
                    {t.readyToRedeem}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRedeem}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 hover:from-amber-300 hover:via-amber-200 hover:to-amber-300 text-stone-950 text-sm font-extrabold tracking-wide shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-400/40 transition-all flex items-center justify-center gap-2 group"
                  data-testid="floating-points-redeem"
                >
                  <Gift className="w-4 h-4 group-hover:rotate-[-8deg] transition-transform" />
                  {t.redeemNow}
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </>
            ) : (
              <>
                {nextTier && (
                  <div className="text-xs text-stone-300 mb-3">
                    <span className="text-amber-200 font-semibold tabular-nums">
                      {pointsToNext.toLocaleString()}
                    </span>{" "}
                    {t.toNextTier}
                  </div>
                )}
                <div className="w-full h-1.5 rounded-full bg-stone-800 overflow-hidden mb-3">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all"
                    style={{
                      width: nextTier
                        ? `${Math.min(100, (points / nextTier.points) * 100)}%`
                        : "100%",
                    }}
                  />
                </div>
              </>
            )}
            <button
              type="button"
              onClick={handleDashboard}
              className="w-full text-center text-[11px] text-amber-200/80 hover:text-amber-100 underline-offset-2 hover:underline mt-2 transition-colors"
              data-testid="floating-points-view-dashboard"
            >
              {t.viewDashboard}
            </button>
          </div>
        </div>
      )}

      {/* The puck itself */}
      <button
        type="button"
        onClick={handlePuckClick}
        className={`relative group flex items-center gap-2 px-4 py-2.5 rounded-full shadow-xl transition-all duration-300 hover:scale-[1.04] active:scale-[0.97] ${
          pulse && !expanded
            ? "shadow-amber-400/60 ring-2 ring-amber-300/60"
            : "shadow-amber-900/40"
        }`}
        style={{
          background:
            "linear-gradient(135deg, #1a1a1a 0%, #2d2118 50%, #3d2b1a 100%)",
          border: "1px solid rgba(212,165,116,0.55)",
        }}
        data-testid="floating-points-puck"
        aria-label={isLoggedIn ? `${points} Crown Points — redeem` : t.signedOutTitle}
      >
        <span
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-transform ${
            pulse && !expanded ? "animate-pulse" : ""
          }`}
          style={{
            background: "linear-gradient(135deg, #f3d69b, #d4a574)",
            boxShadow: "0 0 10px rgba(212,165,116,0.55)",
          }}
        >
          <Crown className="w-3.5 h-3.5 text-stone-900" />
        </span>
        <span className="flex flex-col items-start leading-tight pr-1">
          <span
            className="text-[10px] font-bold uppercase tracking-[0.14em]"
            style={{ color: "rgba(212,165,116,0.78)" }}
          >
            {t.mini}
          </span>
          <span
            className="text-sm font-bold tabular-nums"
            style={{ color: "#f3d69b", fontFamily: "'Playfair Display', serif" }}
            data-testid="floating-points-puck-balance"
          >
            {isLoggedIn
              ? `${points.toLocaleString()}${canRedeem ? ` • $${creditValue}` : ""}`
              : t.signedOutCta}
          </span>
        </span>
        {/* Dismiss x — appears only on hover so the puck stays clean. The
            container is `pointer-events-none` so it never intercepts the
            primary click; we re-enable pointer events only when visible. */}
        <span
          role="button"
          tabIndex={0}
          onClick={dismiss}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              dismiss(e);
            }
          }}
          className="pointer-events-none opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity w-5 h-5 rounded-full flex items-center justify-center hover:bg-amber-500/15"
          data-testid="floating-points-dismiss"
          aria-label={t.dismiss}
        >
          <X className="w-3 h-3 text-amber-200/60" />
        </span>
      </button>
    </div>
  );
}
